import type { GameModule } from "../../core/types";
import { getBestScore } from "../../core/Storage";
import { goGame } from "../routes";
import { Api } from "../../core/Api";

type User = { id: number; email: string } | null;

export class HubView {
  private el?: HTMLDivElement;

  private user: User = null;

  constructor(private uiRoot: HTMLElement, private modules: GameModule[]) {}

  mount() {
    const wrap = document.createElement("div");
    wrap.className = "ui-panel";
    wrap.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap;">
        <div>
          <div style="font-weight:800;font-size:18px;">Jeux</div>
          <div style="color:var(--muted);font-size:13px;margin-top:2px;">
            Mini-jeux rapides, jouables au clavier ou au tactile.
          </div>
        </div>

        <div class="card" style="min-width:280px;max-width:360px;">
          <div style="display:flex;justify-content:space-between;gap:10px;align-items:flex-start;">
            <div>
              <div style="font-weight:800;">Compte</div>
              <div id="auth-status" style="color:var(--muted);font-size:12px;margin-top:2px;">
                Chargement...
              </div>
            </div>
            <button class="btn" id="btn-logout" style="display:none;">Déconnexion</button>
          </div>

          <!-- Bouton d’entrée -->
          <div id="auth-actions" style="margin-top:10px;">
            <button class="btn btn-primary" id="btn-open-login">Se connecter</button>
          </div>

          <!-- Formulaire caché -->
          <div id="auth-forms" style="margin-top:12px;display:none;">
            <div class="field">
              <label>Email</label>
              <input class="input" id="auth-email" type="email" placeholder="ton@email.fr" />
            </div>

            <div class="field">
              <label>Mot de passe</label>
              <input class="input" id="auth-pass" type="password" placeholder="••••••••" />
              <div style="font-size:12px;color:var(--muted);">8 caractères minimum</div>
            </div>

            <div class="row">
              <button class="btn btn-primary" id="btn-login">Connexion</button>
              <button class="btn" id="btn-register">Créer un compte</button>
              <button class="btn" id="btn-cancel-login">Annuler</button>
            </div>

            <div id="auth-msg"></div>
          </div>
        </div>

      </div>

      <div class="grid" id="games-grid"></div>
    `;

    const grid = wrap.querySelector("#games-grid") as HTMLDivElement;

    for (const m of this.modules) {
      const best = getBestScore(m.id);
      const card = document.createElement("div");
      card.className = "card";
      card.innerHTML = `
        <h3>${m.title}</h3>
        <p>${m.description}</p>
        <p style="margin:0 0 10px;color:var(--muted);font-size:12px;">Record local : <b style="color:var(--text)">${best}</b></p>
        <button class="btn btn-primary">Jouer</button>
      `;
      card.querySelector("button")!.addEventListener("click", () => goGame(m.id));
      grid.appendChild(card);
    }

    this.uiRoot.appendChild(wrap);
    this.el = wrap;

    // bind auth
    this.bindAuthUI(wrap);

    // load session
    this.refreshMe();
  }

  unmount() {
    this.el?.remove();
    this.el = undefined;
  }

  private bindAuthUI(root: HTMLElement) {
    const status = root.querySelector("#auth-status") as HTMLDivElement;
    const forms = root.querySelector("#auth-forms") as HTMLDivElement;
    const btnLogout = root.querySelector("#btn-logout") as HTMLButtonElement;

    const email = root.querySelector("#auth-email") as HTMLInputElement;
    const pass = root.querySelector("#auth-pass") as HTMLInputElement;

    const btnLogin = root.querySelector("#btn-login") as HTMLButtonElement;
    const btnRegister = root.querySelector("#btn-register") as HTMLButtonElement;
    const msg = root.querySelector("#auth-msg") as HTMLDivElement;

    const actions = root.querySelector("#auth-actions") as HTMLDivElement;
    const btnOpen = root.querySelector("#btn-open-login") as HTMLButtonElement;
    const btnCancel = root.querySelector("#btn-cancel-login") as HTMLButtonElement;


    const setMsg = (type: "error" | "success" | "none", text = "") => {
      if (type === "none") {
        msg.innerHTML = "";
        return;
      }
      msg.innerHTML = `<div class="alert ${type === "error" ? "alert-error" : "alert-success"}">${escapeHtml(text)}</div>`;
    };

    const setLoading = (loading: boolean) => {
      btnLogin.disabled = loading;
      btnRegister.disabled = loading;
      btnLogout.disabled = loading;
    };

    const renderAuth = () => {
      if (this.user) {
        status.textContent = `Connecté : ${this.user.email}`;
        forms.style.display = "none";
        actions.style.display = "none";
        btnLogout.style.display = "inline-block";
        setMsg("none");
      } else {
        status.textContent = "Non connecté";
        btnLogout.style.display = "none";
        forms.style.display = "none";
        actions.style.display = "block";
      }
    };


    btnLogin.addEventListener("click", async () => {
      setMsg("none");
      setLoading(true);
      try {
        const e = email.value.trim();
        const p = pass.value;
        const r = await Api.login(e, p);
        this.user = r.user ?? null;
        setMsg("success", "Connexion réussie.");
        renderAuth();
      } catch (err: any) {
        setMsg("error", err?.message || "Connexion impossible");
      } finally {
        setLoading(false);
      }
    });

    btnRegister.addEventListener("click", async () => {
      setMsg("none");
      setLoading(true);
      try {
        const e = email.value.trim();
        const p = pass.value;
        const r = await Api.register(e, p);
        this.user = r.user ?? null;
        setMsg("success", "Compte créé et connecté.");
        renderAuth();
      } catch (err: any) {
        setMsg("error", err?.message || "Création impossible");
      } finally {
        setLoading(false);
      }
    });

    btnLogout.addEventListener("click", async () => {
      setMsg("none");
      setLoading(true);
      try {
        await Api.logout();
        this.user = null;
        setMsg("success", "Déconnecté.");
        renderAuth();
      } catch (err: any) {
        setMsg("error", err?.message || "Déconnexion impossible");
      } finally {
        setLoading(false);
      }
    });
    btnOpen.addEventListener("click", () => {
      forms.style.display = "block";
      actions.style.display = "none";
      setMsg("none");
    });

    btnCancel.addEventListener("click", () => {
      forms.style.display = "none";
      actions.style.display = "block";
      setMsg("none");
    });


    // premier rendu
    renderAuth();
  }

  private async refreshMe() {
    if (!this.el) return;
    const status = this.el.querySelector("#auth-status") as HTMLDivElement;

    try {
      const r = await Api.me();
      this.user = r.user ?? null;
    } catch {
      // si l’API est down, on reste offline
      this.user = null;
      status.textContent = "API indisponible";
      return;
    }

    // force re-render auth UI
    const forms = this.el.querySelector("#auth-forms") as HTMLDivElement;
    const btnLogout = this.el.querySelector("#btn-logout") as HTMLButtonElement;

    if (this.user) {
      status.textContent = `Connecté : ${this.user.email}`;
      forms.style.display = "none";
      btnLogout.style.display = "inline-block";
    } else {
      status.textContent = "Non connecté";
      forms.style.display = "block";
      btnLogout.style.display = "none";
    }
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#039;";
      default: return c;
    }
  });
}
